"use client"

import { useSelector, useDispatch } from 'react-redux'
import { useEffect } from 'react'
import {
	loginUser,
	adminLoginUser,
	registerUser,
	logoutUser,
	checkAuth,
	clearError,
	checkAuthAdmin,
	checkAuthAuthor,
	checkAuthAdManager,
	setInitialized,
} from '@/store/slices/authSlice'
import { usePathname } from 'next/navigation'
import {
	ADMIN_AUTH_COOKIE_NAME,
	AUTH_COOKIE_NAME,
	AUTHOR_AUTH_COOKIE_NAME,
	ADMANAGER_AUTH_COOKIE_NAME,
} from '@/lib/auth-cookie'
import Cookies from 'js-cookie'

export const useReduxAuth = () => {
	const dispatch = useDispatch()
	const {
		user,
		token,
		loading,
		error,
		isAuthenticated,
		initialized
	} = useSelector((state) => state.auth)

	const pathname = usePathname()
	const userToken = Cookies.get(AUTH_COOKIE_NAME)
	const adminToken = Cookies.get(ADMIN_AUTH_COOKIE_NAME)
	const authorToken = Cookies.get(AUTHOR_AUTH_COOKIE_NAME)
	const adManagerToken = Cookies.get(ADMANAGER_AUTH_COOKIE_NAME)

	useEffect(() => {
		if (initialized) return

		if (pathname?.includes('/admin')) {
			if (adminToken) dispatch(checkAuthAdmin())
			else dispatch(setInitialized())
			return
		}

		if (pathname?.includes('/admanager')) {
			if (adManagerToken) dispatch(checkAuthAdManager())
			else dispatch(setInitialized())
			return
		}

		if (pathname?.includes('/author')) {
			const isAuthorApp =
				pathname === '/author' ||
				pathname.startsWith('/author/login') ||
				pathname.startsWith('/author/news') ||
				pathname.startsWith('/author/profile') ||
				pathname.startsWith('/author/districts') ||
				pathname.startsWith('/author/subdivisions') ||
				pathname.startsWith('/author/tehsils') ||
				pathname.startsWith('/author/location-list')

			if (isAuthorApp) {
				if (authorToken) dispatch(checkAuthAuthor())
				else dispatch(setInitialized())
				return
			}
		}

		if (userToken) dispatch(checkAuth())
		else dispatch(setInitialized())
	}, [dispatch, initialized, pathname, userToken, adminToken, authorToken, adManagerToken])

	const login = async (email, password) => {
		const action = await dispatch(loginUser({ email, password }))
		if (loginUser.fulfilled.match(action)) {
			return { success: true, ...action.payload }
		}
		return { success: false, error: action.payload || 'Login failed' }
	}

	const adminLogin = async (email, password, type) => {
		const action = await dispatch(adminLoginUser({ email, password, type }))
		if (adminLoginUser.fulfilled.match(action)) {
			return { success: true, ...action.payload }
		}
		return { success: false, error: action.payload || 'Admin login failed' }
	}

	const register = async (name, email, password) => {
		const action = await dispatch(registerUser({ name, email, password }))
		if (registerUser.fulfilled.match(action)) {
			return { success: true, ...action.payload }
		}
		return { success: false, error: action.payload || 'Registration failed' }
	}

	const logout = async () => {
		await dispatch(logoutUser())
	}

	const clearAuthError = () => {
		dispatch(clearError())
	}

	return {
		user,
		token,
		loading,
		error,
		isAuthenticated,
		initialized,
		login,
		adminLogin,
		register,
		logout,
		clearError: clearAuthError
	}
}
